(() => {
  'use strict';

  const logic = window.EdiBotProductLogic;
  if (!logic || document.documentElement.dataset.edpInitialized === 'true') return;
  document.documentElement.dataset.edpInitialized = 'true';

  let config;
  let state;
  let stickyObserver;
  let stickyFallbackInstalled = false;
  let stickyFallbackHandler;
  let isSubmitting = false;
  let viewportVideoObserver;
  let viewportVideoEntries = new Map();
  let activeViewportVideo;

  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const galleryRuntime = new WeakMap();
  const mediaWarmPromises = new WeakMap();
  const MEDIA_EAGER_WINDOW = 3;
  const MEDIA_READY_TIMEOUT = 4000;
  const VIEWPORT_VIDEO_MIN_RATIO = 0.55;

  function initializeState(force = false) {
    const configNode = document.querySelector('[data-edp-config]');
    if (!configNode) return false;
    if (state && !force) return true;

    try {
      config = JSON.parse(configNode.textContent);
      state = logic.createState(config);
    } catch (error) {
      console.error('Could not initialize the EdiBot product page.', error);
      return false;
    }

    return true;
  }

  function updateCtas(previewMode, unavailable) {
    all('[data-edp-add]').forEach((button) => {
      button.disabled = previewMode || unavailable || isSubmitting;
      button.setAttribute('aria-disabled', String(button.disabled));
      if (!isSubmitting) button.textContent = unavailable ? 'Sold out' : 'Add to cart';
    });

    all('[data-edp-buy]').forEach((button) => {
      button.disabled = previewMode || unavailable || isSubmitting;
      button.setAttribute('aria-disabled', String(button.disabled));
      if (!isSubmitting) button.textContent = unavailable ? 'Sold out' : 'Buy now';
    });

    all('[data-edp-preview-note]').forEach((node) => {
      node.hidden = !previewMode;
    });

    const showEmptyMedia = previewMode || Boolean(window.Shopify && window.Shopify.designMode);
    all('.edp-media-slot.is-placeholder').forEach((slot) => {
      slot.hidden = !showEmptyMedia;
    });
  }

  function render() {
    if (!state) return;

    const set = state.config.sets[state.selectedSet];
    const price = logic.getDisplayPrice(state);
    const variant = logic.resolveVariant(state);

    all('[data-edp-set]').forEach((button) => {
      const active = button.dataset.edpSet === state.selectedSet;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    all('[data-edp-color]').forEach((button) => {
      const active = button.dataset.edpColor === state.selectedColor;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    all('[data-edp-selected-set]').forEach((node) => {
      node.textContent = set.name;
    });
    all('[data-edp-selected-color]').forEach((node) => {
      node.textContent = state.selectedColor;
    });
    all('[data-edp-price]').forEach((node) => {
      node.textContent = logic.formatMoney(price.total, config.money);
    });
    all('[data-edp-compare-price]').forEach((node) => {
      node.hidden = !price.compareAt;
      node.textContent = price.compareAt ? logic.formatMoney(price.compareAt, config.money) : '';
    });
    all('[data-edp-set-price]').forEach((node) => {
      const setPrice = logic.getSetPrice(state, node.dataset.edpSetPrice);
      node.textContent = setPrice ? logic.formatMoney(setPrice.price, config.money) : '';
    });
    all('[data-edp-set-compare-price]').forEach((node) => {
      const setPrice = logic.getSetPrice(state, node.dataset.edpSetComparePrice);
      node.hidden = !setPrice || !setPrice.compareAt;
      node.textContent = setPrice && setPrice.compareAt
        ? logic.formatMoney(setPrice.compareAt, config.money)
        : '';
    });
    all('[data-edp-ink-price]').forEach((node) => {
      node.textContent = logic.formatMoney(config.inkCare.price, config.money);
    });

    all('[data-edp-ink-panel]').forEach((node) => {
      node.hidden = set.inkCareIncluded;
    });
    all('[data-edp-ink-included]').forEach((node) => {
      node.hidden = !set.inkCareIncluded;
    });
    all('[data-edp-ink-care]').forEach((input) => {
      input.checked = state.inkCareSelected;
      input.disabled = !config.inkCare.variantId || !config.inkCare.available;
      input.setAttribute(
        'aria-label',
        `Add 1-Year Ink Care for ${logic.formatMoney(config.inkCare.price, config.money)}`
      );
    });
    all('[data-edp-creator-rewards]').forEach((input) => {
      input.checked = state.creatorRewardsSelected;
    });
    all('[data-edp-ink-status]').forEach((node) => {
      node.textContent = set.inkCareIncluded
        ? 'Included with your selected set'
        : state.inkCareSelected
          ? 'Added to your First Moment Set'
          : `Optional for the First Moment Set · +${logic.formatMoney(config.inkCare.price, config.money)}`;
    });

    all('[data-edp-box-list]').forEach((list) => {
      const items = logic.getBoxContents(state).map((item) => {
        const listItem = document.createElement('li');
        listItem.textContent = item;
        return listItem;
      });
      list.replaceChildren(...items);
    });
    all('[data-edp-compare-set]').forEach((node) => {
      node.classList.toggle('is-selected', node.dataset.edpCompareSet === state.selectedSet);
    });
    all('[data-edp-flatlay]').forEach((node) => {
      const active = node.dataset.edpFlatlay === state.selectedSet;
      node.hidden = !active;
      node.classList.toggle('is-active', active);
    });
    all('[data-edp-final-status]').forEach((node) => {
      if (set.inkCareIncluded) {
        const launchPrefix = state.selectedSet === 'family' && state.config.launchOfferActive
          ? 'Launch offer · '
          : '';
        node.textContent = `${launchPrefix}Ink Care included`;
      } else {
        node.textContent = state.inkCareSelected
          ? `Ink Care added · ${logic.formatMoney(config.inkCare.price, config.money)}`
          : `Ink Care optional · ${logic.formatMoney(config.inkCare.price, config.money)}`;
      }
    });

    const unavailable = !config.previewMode && (!variant || !variant.available);
    updateCtas(Boolean(config.previewMode), unavailable);

    document.dispatchEvent(
      new CustomEvent('edibot:change', {
        detail: { state, set, price, variant },
      })
    );
  }

  async function addSelectionToCart(currentState) {
    if (currentState.config.previewMode) throw new Error('Preview mode cannot add to cart.');

    const items = logic.buildCartItems(currentState);

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const body = await response.json();
    if (!response.ok) throw new Error(body.description || body.message || 'Could not add this set.');
    return body;
  }

  function setPurchaseStatus(message, isError = false) {
    all('[data-edp-purchase-status]').forEach((node) => {
      node.textContent = message;
      node.classList.toggle('is-error', isError);
    });
  }

  function setSubmitting(sourceButton, submitting) {
    isSubmitting = submitting;
    all('[data-edp-add], [data-edp-buy]').forEach((button) => {
      button.setAttribute('aria-busy', String(submitting));
    });
    if (submitting && sourceButton) {
      sourceButton.textContent = 'Adding…';
      sourceButton.disabled = true;
      sourceButton.setAttribute('aria-disabled', 'true');
    } else {
      render();
    }
  }

  async function submitSelection(sourceButton, buyNow) {
    if (!state || isSubmitting) return;

    setSubmitting(sourceButton, true);
    setPurchaseStatus('');
    try {
      await addSelectionToCart(state);
      if (buyNow) {
        window.location.assign('/checkout');
        return;
      }
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } })
      );
      setPurchaseStatus('Your selected set was added to the cart.');
    } catch (error) {
      setPurchaseStatus(error.message || 'Could not add this set.', true);
    } finally {
      setSubmitting(sourceButton, false);
    }
  }

  function startBestEffortDecode(image) {
    if (!image || typeof image.decode !== 'function') return;
    try {
      const decoding = image.decode();
      if (decoding && typeof decoding.catch === 'function') {
        decoding.catch(() => {});
      }
    } catch (_error) {
      // Decode is only an enhancement; readiness must not depend on it.
    }
  }

  function getGalleryRuntime(group) {
    let runtime = galleryRuntime.get(group);
    if (!runtime) {
      runtime = { requestId: 0, warmQueueStarted: false };
      galleryRuntime.set(group, runtime);
    }
    return runtime;
  }

  function getMediaPanelForTab(tab) {
    const group = tab.closest('[data-edp-media-group]');
    if (!group) return null;
    const target = tab.dataset.edpMediaTab || tab.getAttribute('aria-controls');
    if (!target) return null;
    return all('[data-edp-media-panel]', group).find(
      (panel) => panel.dataset.edpMediaPanel === target || panel.id === target
    ) || null;
  }

  function warmMediaForTab(tab, priority = 'low') {
    const panel = getMediaPanelForTab(tab);
    if (!panel) return Promise.resolve();

    const image = panel.querySelector('img');
    if (!image) return Promise.resolve();

    image.loading = 'eager';
    image.fetchPriority = priority;

    const existing = mediaWarmPromises.get(image);
    if (existing) return existing;

    const promise = new Promise((resolve) => {
      let settled = false;
      let timeoutId;

      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        resolve();
        if (image.complete && image.naturalWidth > 0) {
          startBestEffortDecode(image);
        }
      };

      timeoutId = window.setTimeout(finish, MEDIA_READY_TIMEOUT);
      if (image.complete) {
        finish();
      } else {
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
      }
    });

    mediaWarmPromises.set(image, promise);
    return promise;
  }

  function commitMediaTab(tab, moveFocus = false) {
    const group = tab.closest('[data-edp-media-group]');
    if (!group) return;
    const target = tab.dataset.edpMediaTab || tab.getAttribute('aria-controls');
    if (!target) return;

    all('[data-edp-media-tab]', group).forEach((button) => {
      const active = button === tab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
      button.setAttribute('tabindex', active ? '0' : '-1');
    });
    all('[data-edp-media-panel]', group).forEach((panel) => {
      const active = panel.dataset.edpMediaPanel === target || panel.id === target;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
      if (!active) all('video', panel).forEach((video) => video.pause());
    });

    tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (moveFocus) tab.focus();
  }

  async function requestMediaActivation(tab, moveFocus = false) {
    const group = tab.closest('[data-edp-media-group]');
    if (!group) return;
    const runtime = getGalleryRuntime(group);
    runtime.requestId += 1;
    const requestId = runtime.requestId;

    const panel = getMediaPanelForTab(tab);
    const image = panel ? panel.querySelector('img') : null;
    if (image && !(image.complete && image.naturalWidth > 0)) {
      await warmMediaForTab(tab, 'high');
    }

    if (requestId !== runtime.requestId) return;
    if (!tab.isConnected || !group.isConnected) return;
    commitMediaTab(tab, moveFocus);
  }

  function scheduleMediaWarmQueue(group, activeIndex) {
    const runtime = getGalleryRuntime(group);
    if (runtime.warmQueueStarted) return;
    runtime.warmQueueStarted = true;

    const tabs = all('[data-edp-media-tab]', group);
    const order = logic.getMediaPreloadOrder(activeIndex, tabs.length);
    order.slice(0, MEDIA_EAGER_WINDOW).forEach((index) => {
      const tab = tabs[index];
      if (tab) warmMediaForTab(tab, 'low');
    });

    const idleOrder = order.slice(MEDIA_EAGER_WINDOW);
    const warmNext = () => {
      if (!group.isConnected) return;
      const index = idleOrder.shift();
      if (!Number.isInteger(index)) return;
      const tab = tabs[index];
      const continueQueue = () => scheduleNext();
      if (!tab || !tab.isConnected) {
        continueQueue();
        return;
      }
      warmMediaForTab(tab, 'low').then(continueQueue);
    };
    const scheduleNext = () => {
      if (!idleOrder.length || !group.isConnected) return;
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warmNext, { timeout: 1800 });
      } else {
        window.setTimeout(warmNext, 250);
      }
    };
    scheduleNext();
  }

  function initializeMediaGroups(root = document) {
    const groups = [];
    if (root.matches && root.matches('[data-edp-media-group]')) groups.push(root);
    all('[data-edp-media-group]', root).forEach((group) => groups.push(group));
    groups.forEach((group) => {
      const firstTab = group.querySelector('[data-edp-media-tab]');
      if (!firstTab) return;
      commitMediaTab(firstTab);
      scheduleMediaWarmQueue(group, 0);
    });
  }

  function pausePanelVideos(panel) {
    panel.querySelectorAll('video').forEach((video) => video.pause());
  }

  function activateContentTab(tab, moveFocus = false) {
    const group = tab.closest('[data-edp-tabs]');
    if (!group) return;

    const value = tab.dataset.edpTab;
    all('[data-edp-tab]', group).forEach((button) => {
      const active = button.dataset.edpTab === value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
      button.setAttribute('tabindex', active ? '0' : '-1');
    });
    all('[data-edp-tab-panel]', group).forEach((panel) => {
      const active = panel.dataset.edpTabPanel === value;
      if (!active) pausePanelVideos(panel);
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });

    if (moveFocus) tab.focus();
    window.requestAnimationFrame(refreshViewportVideoPlayback);
  }

  function shouldDisableViewportAutoplay(connection, reducedMotion, supportsObserver) {
    const effectiveType = connection && connection.effectiveType ? connection.effectiveType : '';
    return !supportsObserver || Boolean(reducedMotion) || Boolean(connection && connection.saveData) ||
      ['slow-2g', '2g', '3g'].includes(effectiveType);
  }

  function selectViewportVideoCandidate(candidates, viewportCenter) {
    const eligible = candidates.filter((candidate) => candidate.eligible && candidate.ratio >= 0.55);
    eligible.sort((left, right) => {
      if (right.ratio !== left.ratio) return right.ratio - left.ratio;
      return Math.abs(left.center - viewportCenter) - Math.abs(right.center - viewportCenter);
    });
    return eligible.length ? eligible[0].video : null;
  }

  function pauseManagedViewportVideos(root = document) {
    root.querySelectorAll('[data-edp-viewport-video] video').forEach((video) => video.pause());
  }

  function refreshViewportVideoPlayback() {
    const wrappers = all('[data-edp-viewport-video]');
    if (!wrappers.length) {
      activeViewportVideo = undefined;
      return;
    }

    if (document.hidden) {
      pauseManagedViewportVideos();
      activeViewportVideo = undefined;
      return;
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const reducedMotion = Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const autoplayDisabled = shouldDisableViewportAutoplay(
      connection,
      reducedMotion,
      'IntersectionObserver' in window
    );

    if (autoplayDisabled) {
      wrappers.forEach((wrapper) => {
        const panel = wrapper.closest('[data-edp-tab-panel]');
        const entry = viewportVideoEntries.get(wrapper);
        const video = wrapper.querySelector('video');
        if (video && ((panel && panel.hidden) || (entry && entry.ratio === 0))) video.pause();
      });
      if (activeViewportVideo && activeViewportVideo.paused) activeViewportVideo = undefined;
      return;
    }

    const candidates = wrappers.map((wrapper) => {
      const entry = viewportVideoEntries.get(wrapper);
      const panel = wrapper.closest('[data-edp-tab-panel]');
      return {
        video: wrapper.querySelector('video'),
        ratio: entry ? entry.ratio : 0,
        center: entry ? entry.center : Number.POSITIVE_INFINITY,
        eligible: Boolean(entry && entry.isIntersecting && (!panel || !panel.hidden)),
      };
    }).filter((candidate) => candidate.video);

    const winner = selectViewportVideoCandidate(candidates, window.innerHeight / 2);
    candidates.forEach((candidate) => {
      if (candidate.video !== winner) candidate.video.pause();
    });

    activeViewportVideo = winner || undefined;
    if (!winner) return;

    winner.muted = true;
    winner.defaultMuted = true;
    winner.playsInline = true;
    if (winner.paused) {
      const playPromise = winner.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    }
  }

  function initializeViewportVideos() {
    if (viewportVideoObserver) viewportVideoObserver.disconnect();
    viewportVideoEntries = new Map();
    activeViewportVideo = undefined;

    const wrappers = all('[data-edp-viewport-video]');
    if (!wrappers.length || !('IntersectionObserver' in window)) return;

    viewportVideoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        viewportVideoEntries.set(entry.target, {
          ratio: entry.intersectionRatio,
          center: entry.boundingClientRect.top + (entry.boundingClientRect.height / 2),
          isIntersecting: entry.isIntersecting,
        });
      });
      refreshViewportVideoPlayback();
    }, { threshold: [0, 0.25, VIEWPORT_VIDEO_MIN_RATIO, 0.75, 1] });

    wrappers.forEach((wrapper) => viewportVideoObserver.observe(wrapper));
  }

  function toggleControlledPanel(button) {
    const targetId = button.getAttribute('aria-controls') || button.dataset.edpTarget;
    const panel = targetId ? document.getElementById(targetId) : button.nextElementSibling;
    if (!panel) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  }

  function scrollToSelector() {
    const selector = document.querySelector('[data-edp-selector]');
    if (!selector) return;
    selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusTarget = selector.querySelector('[data-edp-set].is-active, [data-edp-set]');
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  function updateStickyVisibility(visible) {
    all('[data-edp-sticky], .edp-sticky').forEach((sticky) => {
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
    });
  }

  function installStickyVisibility() {
    const selector = document.querySelector('[data-edp-selector]');
    if (!selector) return;

    if (stickyObserver) stickyObserver.disconnect();
    if ('IntersectionObserver' in window) {
      stickyObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const passed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 120;
          updateStickyVisibility(passed);
        },
        { rootMargin: '-120px 0px 0px 0px', threshold: 0 }
      );
      stickyObserver.observe(selector);
      return;
    }

    stickyFallbackHandler = () => {
      const currentSelector = document.querySelector('[data-edp-selector]');
      updateStickyVisibility(Boolean(currentSelector && currentSelector.getBoundingClientRect().bottom <= 120));
    };
    if (!stickyFallbackInstalled) {
      window.addEventListener('scroll', () => stickyFallbackHandler(), { passive: true });
      window.addEventListener('resize', () => stickyFallbackHandler());
      stickyFallbackInstalled = true;
    }
    stickyFallbackHandler();
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const setButton = target.closest('[data-edp-set]');
    const colorButton = target.closest('[data-edp-color]');
    const mediaTab = target.closest('[data-edp-media-tab]');
    const contentTab = target.closest('[data-edp-tab]');
    const disclosure = target.closest('[data-edp-disclosure], [data-edp-accordion]');
    const productDetailsSummary = target.closest('[data-edp-product-details] summary');
    const scrollButton = target.closest('[data-edp-scroll-to-selector]');
    const addButton = target.closest('[data-edp-add]');
    const buyButton = target.closest('[data-edp-buy]');

    if (setButton && state) {
      state = logic.selectSet(state, setButton.dataset.edpSet);
      render();
    }
    if (colorButton && state) {
      state = logic.selectColor(state, colorButton.dataset.edpColor);
      render();
    }
    if (mediaTab) requestMediaActivation(mediaTab);
    if (contentTab) activateContentTab(contentTab);
    if (disclosure && disclosure.tagName !== 'SUMMARY') toggleControlledPanel(disclosure);
    if (productDetailsSummary) {
      event.preventDefault();
      const productDetails = productDetailsSummary.closest('[data-edp-product-details]');
      if (productDetails) {
        const shouldOpen = !productDetails.open;
        requestAnimationFrame(() => {
          productDetails.open = shouldOpen;
        });
      }
    }
    if (scrollButton) scrollToSelector();
    if (addButton) submitSelection(addButton, false);
    if (buyButton) submitSelection(buyButton, true);
  });

  document.addEventListener('keydown', (event) => {
    const mediaTab = event.target instanceof Element ? event.target.closest('[data-edp-media-tab]') : null;
    const mediaKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (mediaTab && mediaKeys.includes(event.key)) {
      const mediaGroup = mediaTab.closest('[data-edp-media-group]');
      const mediaTabs = mediaGroup ? all('[data-edp-media-tab]', mediaGroup) : [];
      const currentIndex = mediaTabs.indexOf(mediaTab);
      const nextIndex = logic.getMediaNavigationIndex(currentIndex, mediaTabs.length, event.key);
      const nextTab = mediaTabs[nextIndex];
      if (nextTab) {
        event.preventDefault();
        requestMediaActivation(nextTab, true);
      }
      return;
    }

    const tab = event.target instanceof Element ? event.target.closest('[data-edp-tab]') : null;
    const contentTabKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!tab || !contentTabKeys.includes(event.key)) return;

    const group = tab.closest('[data-edp-tabs]');
    const tabs = group ? all('[data-edp-tab]', group) : [];
    if (!tabs.length) return;

    event.preventDefault();
    const currentIndex = tabs.indexOf(tab);
    const nextIndex = logic.getMediaNavigationIndex(currentIndex, tabs.length, event.key);
    if (tabs[nextIndex]) activateContentTab(tabs[nextIndex], true);
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof Element) || !state) return;
    if (input.matches('[data-edp-ink-care]')) {
      state = logic.setInkCare(state, input.checked);
      render();
    }
    if (input.matches('[data-edp-creator-rewards]')) {
      state = logic.setCreatorRewards(state, input.checked);
      render();
    }
  });

  function warmMediaFromIntent(event) {
    const target = event.target instanceof Element ? event.target : null;
    const mediaTab = target ? target.closest('[data-edp-media-tab]') : null;
    if (mediaTab) warmMediaForTab(mediaTab, 'high');
  }

  ['pointerenter', 'pointerdown', 'focusin'].forEach((eventName) => {
    document.addEventListener(eventName, warmMediaFromIntent, true);
  });

  document.addEventListener('visibilitychange', refreshViewportVideoPlayback);

  document.addEventListener('shopify:section:load', (event) => {
    const reloadedConfig = event.target && event.target.querySelector
      ? event.target.querySelector('[data-edp-config]')
      : null;
    if (reloadedConfig) initializeState(true);
    initializeMediaGroups(event.target);
    render();
    initializeViewportVideos();
    installStickyVisibility();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    pauseManagedViewportVideos(event.target);
    window.requestAnimationFrame(initializeViewportVideos);
  });

  if (initializeState()) {
    initializeMediaGroups();
    render();
    initializeViewportVideos();
    installStickyVisibility();
  }
})();
